import { INotificationsService } from '~/rage/services';
import { IObservable, Subject } from '~/rage/utils';
import { IClientConfigs, IDialogueReplyAction, RewardType } from '~/shared';
import {
	DialogueReward,
	IActionCallback,
	IDialogueService,
	IDialogueState,
} from './dialogueServiceTypes';

export abstract class AbstractDialogueService implements IDialogueService {
	protected npcName = new Subject<string>('???');
	protected dialogueId = new Subject<string | null>(null);

	protected dialogueState = new Subject<IDialogueState | null>(null);
	private registeredActions = new Map<string, IActionCallback[]>();

	constructor(protected readonly notifications: INotificationsService) {
		this.dialogueId.subscribe(() => this.updateDialogueState());
	}

	protected abstract getConfig(): IClientConfigs['quests']['dialogues'];

	public onAction(name: string, cb: IActionCallback): void {
		const callbacks = this.registeredActions.get(name) || [];
		callbacks.push(cb);
		this.registeredActions.set(name, callbacks);
	}

	/**
	 * Получить конфиг по id диалога
	 */
	protected getDialogueConfig(dialogueId: string) {
		return this.getConfig().find((e) => e.id === dialogueId) || null;
	}

	/**
	 * Обновление subject'а с состоянием диалога
	 */
	protected updateDialogueState(): void {
		const currentDialogueId = this.dialogueId.getValue();
		if (!currentDialogueId) {
			this.dialogueState.next(null);
			return;
		}

		const config = this.getDialogueConfig(currentDialogueId);
		if (!config) {
			return;
		}

		let title = config.title;
		if (config.title.includes('{npc_name}')) {
			const npcName = this.npcName.getValue();
			title = config.title.replace('{npc_name}', npcName);
		}

		const rewards = (config.reward ?? []).map(
			([type, value]: [RewardType, string]) => {
				const amount = parseInt(value, 10) || 0;
				return {
					type:
						type === RewardType.EXP ? DialogueReward.Exp : DialogueReward.Money,
					amount,
				};
			},
		);

		this.dialogueState.next({
			title,
			text: config.text,
			replies: config.replies.map((replyConfig, index) => ({
				id: `${currentDialogueId}_${index}`,
				text: replyConfig.text,
				icon: replyConfig.icon,
			})),
			rewards,
		});
	}

	public async selectReply(replyId: string): Promise<void> {
		const currentDialogueId = this.dialogueId.getValue();
		if (!currentDialogueId) {
			return;
		}

		const config = this.getDialogueConfig(currentDialogueId);
		if (!config) {
			return;
		}

		const replyIndex = parseInt(replyId.split('_').pop() || '', 10);
		const reply = config.replies[replyIndex];
		if (!reply) {
			return;
		}

		const actions = reply.actions;
		if (!actions || actions.length === 0) {
			return;
		}

		for (const action of actions) {
			await this.handleAction(action);
		}
	}

	public getDialogueState(): IObservable<IDialogueState | null> {
		return this.dialogueState;
	}

	private async handleAction(action: IDialogueReplyAction) {
		console.log(
			`[Dialogues] Triggering action "${action.name}" (${action.params.join(
				', ',
			)})`,
		);

		const callbacks = this.registeredActions.get(action.name);
		if (!callbacks) {
			return;
		}

		for (const cb of callbacks) {
			try {
				await cb(...action.params);
			} catch (error) {
				console.error(
					`Error occured while processing quest action callback "${
						action.name
					}" (${action.params.join(',')})`,
				);

				this.notifications?.showException(error);
			}
		}
	}
}
